import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import circleCheckIcon from '@/assets/icons/circleCheck.svg';
import filledNarrativeIcon from '@/assets/icons/filledNarrativeIcon.svg';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

interface NarrativeWalkthroughDialogProps {
  onDone?: () => void;
  onNext?: () => void;
  onOpenChange: (open: boolean) => void;
  onSetUpLater?: () => void;
  onUpload?: () => void;
  open: boolean;
  step: 1 | 2 | 3;
}

export function NarrativeWalkthroughDialog({
  onDone,
  onNext,
  onOpenChange,
  onSetUpLater,
  onUpload,
  open,
  step,
}: NarrativeWalkthroughDialogProps) {
  const { t } = useTranslation();
  if (!open) {
    return null;
  }

  // Step 1: Introduction
  if (step === 1) {
    return (
      <Popover onOpenChange={onOpenChange} open={open}>
        <PopoverTrigger asChild>
          <div className="pointer-events-none fixed top-1/2 left-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2" />
        </PopoverTrigger>
        <PopoverContent
          align="center"
          alignOffset={0}
          className="border-border-subtle bg-popover w-[270px] max-w-[calc(100vw-32px)] rounded-lg border p-0 shadow-lg"
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          side="top"
          sideOffset={-100}
        >
          <div className="flex items-center px-3.5 pt-3.5 pb-2">
            <div className="flex items-center gap-2">
              <img
                alt={t('narrative.walkthrough.step1.iconAlt')}
                className="icon-themed h-4 w-4"
                src={filledNarrativeIcon}
              />
              <h3 className="text-foreground text-[14px] leading-[145%] font-medium tracking-normal">
                {t('narrative.walkthrough.step1.title')}
              </h3>
            </div>
          </div>
          <Separator className="my-0" />
          <div className="px-3.5 pt-3.5 pb-3.5">
            <p className="text-secondary text-center text-xs leading-[155%] tracking-normal">
              {t('narrative.walkthrough.step1.body')}
            </p>
          </div>
          <div className="flex justify-center px-3.5 pb-3.5">
            <Button
              className="bg-primary-active hover:bg-primary-hover text-primary-foreground h-8 w-full text-xs"
              onClick={onNext}
              size="sm"
              variant="default"
            >
              {t('narrative.walkthrough.next')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Step 2: How it works
  if (step === 2) {
    return (
      <Popover onOpenChange={onOpenChange} open={open}>
        <PopoverTrigger asChild>
          <div className="pointer-events-none fixed top-1/2 left-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2" />
        </PopoverTrigger>
        <PopoverContent
          align="center"
          alignOffset={0}
          className="border-border-subtle bg-popover w-[270px] max-w-[calc(100vw-32px)] rounded-lg border p-0 shadow-lg"
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          side="top"
          sideOffset={-100}
        >
          <div className="flex items-center px-3.5 pt-3.5 pb-2">
            <div className="flex items-center gap-2">
              <img
                alt={t('narrative.walkthrough.step2.iconAlt')}
                className="icon-themed h-4 w-4"
                src={filledNarrativeIcon}
              />
              <h3 className="text-foreground text-[14px] leading-[145%] font-medium tracking-normal">
                {t('narrative.walkthrough.step2.title')}
              </h3>
            </div>
          </div>
          <Separator className="my-0" />
          <div className="px-3.5 pt-3.5 pb-3.5">
            <div className="flex flex-col">
              {[
                t('narrative.walkthrough.step2.point1'),
                t('narrative.walkthrough.step2.point2'),
                t('narrative.walkthrough.step2.point3'),
              ].map((text, index, arr) => (
                <div className="flex flex-col" key={index}>
                  <div className="flex items-center gap-2.5">
                    <img
                      alt="Check"
                      className="icon-themed h-4 w-4 flex-shrink-0"
                      src={circleCheckIcon}
                    />
                    <p className="text-secondary flex-1 font-sans text-[12px] leading-[170%] font-medium tracking-[0%]">
                      {text}
                    </p>
                  </div>
                  {index < arr.length - 1 && (
                    <div className="bg-border-strong mt-0.5 mb-0.5 ml-[7.5px] h-4 w-px" />
                  )}
                </div>
              ))}
            </div>
            <div className="bg-warning-bg mt-3 h-[25px] w-[240px] rounded-[4px] p-1">
              <div className="flex items-center gap-1">
                <Info className="text-tertiary h-3 w-3 shrink-0" />
                <p className="text-secondary font-sans text-[11px] leading-[170%] font-medium tracking-[0%]">
                  {t('narrative.walkthrough.step2.banner', {
                    profile: t('narrative.walkthrough.profile'),
                  })}
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2 px-3.5 pb-3.5">
            <Button
              className="h-8 flex-1 text-xs"
              onClick={onSetUpLater}
              size="sm"
              variant="outline"
            >
              {t('narrative.walkthrough.setUpLater')}
            </Button>
            <Button
              className="bg-primary-active hover:bg-primary-hover text-primary-foreground h-8 flex-1 text-xs"
              onClick={onUpload}
              size="sm"
              variant="default"
            >
              {t('narrative.walkthrough.uploadNarratives')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Step 3: Set up later message
  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      <PopoverTrigger asChild>
        <div className="pointer-events-none fixed top-1/2 left-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2" />
      </PopoverTrigger>
      <PopoverContent
        align="center"
        alignOffset={0}
        className="border-border-subtle bg-popover w-[270px] max-w-[calc(100vw-32px)] rounded-lg border p-0 shadow-lg"
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        side="top"
        sideOffset={-100}
      >
        <div className="px-3.5 pt-3.5 pb-3.5">
          <div className="flex flex-col gap-1">
            <p className="text-secondary text-center text-xs leading-[155%] tracking-normal">
              {t('narrative.walkthrough.step3.line1')}
            </p>
            <p className="text-secondary text-center text-xs leading-[155%] font-semibold tracking-normal">
              {t('narrative.walkthrough.step3.line2')}
            </p>
          </div>
        </div>
        <div className="flex justify-center px-3.5 pb-3.5">
          <Button
            className="bg-primary-active hover:bg-primary-hover text-primary-foreground h-8 w-full text-xs"
            onClick={onDone}
            size="sm"
            variant="default"
          >
            {t('narrative.walkthrough.done')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
